import { beginMapEdit } from './editlock';
// Territorial control (migration 054) — the client half. A territory is a
// polygon ENTITY the tiles already draw; what lives here is the colour each
// faction's ground takes, the Layers legend for the control category, the
// "click a district, reassign it" flow, country tracking, and the proposal
// rows the live feed raises (the `control` advice lane) in the Intel pane.
//
// Colour is keyed by the faction's NAME because that is what the tile carries
// (`faction` and `contested_by`, the canonical spellings the server resolved).
// The colours live in the `territoryColors` setting (062), so an operator can
// make Ansar Allah green everywhere from the legend, whether or not an actor
// entity of that name exists — the ACAPS import names holders that have none,
// and the picker used to need one. A faction entity's `attrs.color` is still
// honoured when the setting names no colour for it. The same setting says how
// CONTESTED ground is hatched: holder's colour, contester's, or one colour.

import * as maplibregl from 'maplibre-gl';
import type { EntityDto, Me } from './api';
import { get, post, put } from './api';
import { relTime, toast } from './hud';
import {
  bumpTiles, DEFAULT_CONTESTED_STYLE, forceTileRefresh, setFactionColors,
  type ContestedStyle,
} from './objects';
import {
  applyControl, controlSuggestions, dismissAdvice, previewControl,
  type ControlPreviewDto, type ControlSuggestionDto,
} from './advice';
import { fmtKm2 } from './geo';
import { LIVE_KINDS } from './livekinds';
import {
  captureLine, editTerritoryBoundary, showPieces, territoryUnderLine,
  type PieceDto, type Pt,
} from './territoryedit';
import {
  areaDefaults, captureTerritoryArea, mergeAreaInputs, sameSlot, showAreaPreview, slotGain,
  type AreaInput, type AreaTake,
} from './territoryarea';

// Saturated hues that still read as a 30 % fill on both basemap themes, each
// far from its neighbours so the first few holders in a country contrast.
// No greys: the first version hashed one faction to slate grey and its whole
// half of Yemen disappeared into the basemap. Kept apart from the age ramp
// (red/orange/yellow) the event dots use.
export const PALETTE = [
  '#1f77b4', '#2ca02c', '#9467bd', '#e6550d', '#17becf', '#8c564b',
  '#bcbd22', '#d62728', '#e377c2', '#2b8a6e',
];

const HEX = /^#[0-9a-f]{6}$/i;

/** The territoryColors setting as the client holds it: faction NAME → colour
 *  (case-insensitive on read, the stored spelling kept for the write-back)
 *  and the contested look. */
export interface TerritoryColorConfig {
  factions: Record<string, string>;
  contested: ContestedStyle;
}

let colorConfig: TerritoryColorConfig = { factions: {}, contested: { ...DEFAULT_CONTESTED_STYLE } };

/** Normalise whatever /api/config (or a settings PUT's reply) carried; a
 *  malformed blob means the defaults, never a throw at boot. */
export function setTerritoryColorConfig(raw: unknown): TerritoryColorConfig {
  const next: TerritoryColorConfig = { factions: {}, contested: { ...DEFAULT_CONTESTED_STYLE } };
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const f = r.factions;
  if (f && typeof f === 'object') {
    for (const [name, color] of Object.entries(f as Record<string, unknown>)) {
      const n = name.trim();
      if (n && typeof color === 'string' && HEX.test(color)) next.factions[n] = color.toLowerCase();
    }
  }
  const c = (r.contested && typeof r.contested === 'object' ? r.contested : {}) as Record<string, unknown>;
  if (c.hatch === 'holder' || c.hatch === 'contester' || c.hatch === 'fixed') next.contested.hatch = c.hatch;
  if (typeof c.color === 'string' && HEX.test(c.color)) next.contested.color = c.color.toLowerCase();
  colorConfig = next;
  return next;
}

export function territoryColorConfig(): TerritoryColorConfig {
  return colorConfig;
}

/** Palette colours by POSITION in the sorted list of names, not by hash: a
 *  hash can hand two holders of one country the same colour, or a dull one,
 *  and there is nothing stable about a colour nobody can see. The assignment
 *  is deterministic for a given set of factions; an operator who wants one
 *  pinned sets attrs.color on the faction. */
export function paletteColors(names: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  [...new Set(names.map((n) => n.trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
    .forEach((n, i) => { out[n] = PALETTE[i % PALETTE.length]!; });
  return out;
}

/** name → colour for every faction that holds OR contests ground in `all`:
 *  the territoryColors setting when it names one (case-insensitively), else
 *  the faction entity's attrs.color when set, else the palette. Keys are the
 *  territories' own spellings, which are the tile's. Contesters are in the
 *  map too, so the contester hatch mode always has a colour to draw. */
export function factionColors(all: EntityDto[],
                              cfg: TerritoryColorConfig = colorConfig): Record<string, string> {
  const pinned = new Map<string, string>();
  for (const [name, color] of Object.entries(cfg.factions)) pinned.set(name.toLowerCase(), color);
  const custom = new Map<string, string>();
  for (const e of all) {
    if (e.type !== 'faction' || !e.name) continue;
    const c = e.attrs.color;
    if (typeof c === 'string' && HEX.test(c)) custom.set(e.name.trim().toLowerCase(), c);
  }
  const names: string[] = [];
  for (const e of all) {
    if (e.type !== 'territory') continue;
    for (const key of ['faction', 'contested_by'] as const) {
      const f = String(e.attrs[key] ?? '').trim();
      if (f) names.push(f);
    }
  }
  const fallback = paletteColors(names);
  const out: Record<string, string> = {};
  for (const f of names) {
    if (!(f in out)) {
      const k = f.toLowerCase();
      out[f] = pinned.get(k) ?? custom.get(k) ?? fallback[f]!;
    }
  }
  return out;
}

export function applyFactionColors(map: maplibregl.Map, all: EntityDto[]): void {
  setFactionColors(map, factionColors(all), colorConfig.contested);
}

/** Write one change to the territoryColors setting and take the server's
 *  merged answer as the new config. Operators may (it is a display default,
 *  in SUPERUSER_SETTING_KEYS). */
async function saveTerritoryColors(patch: { factions?: Record<string, string>;
                                            contested?: Partial<ContestedStyle> }): Promise<void> {
  const res = await put<{ settings?: { territoryColors?: unknown } }>(
    '/api/admin/settings', { territoryColors: patch });
  setTerritoryColorConfig(res.settings?.territoryColors
    ?? { factions: patch.factions ?? colorConfig.factions,
         contested: { ...colorConfig.contested, ...patch.contested } });
}

// --- the district pick ------------------------------------------------------
// A district is picked by clicking the map. The click also lands on whatever
// entity is under it (the territory itself, usually), and the object layer's
// handler would open that card; main.ts asks consumeTerritoryPick() first,
// the way it asks select.ts's tryConsumeMapPick for a card's own pick.
let pickArmed = false;

export function consumeTerritoryPick(): boolean {
  if (!pickArmed) return false;
  pickArmed = false;
  return true;
}

// The legend registers its flows here so a territory's card (select.ts) can
// start the same reassignment or cut without owning a map, a list or a form.
let reassignEntry: (() => void) | null = null;
let splitEntry: ((entity: EntityDto) => void) | null = null;
let areaEntry: ((entity: EntityDto | null) => void) | null = null;
let claimEntry: ((slot: SlotPreset) => void) | null = null;
let resolveEntry: ((entity: EntityDto) => void) | null = null;

/** What a claim is preset to: a territory's card, or a proposal's "Draw it
 *  instead" — only the slot's attrs are read. */
type SlotPreset = Pick<EntityDto, 'attrs'>;

// The territory forms FLOAT over the map, in a card of their own (.terr-float)
// rather than inside the Layers legend: every flow they serve is made of map
// clicks, and a map click closes the Layers pod — so a claim used to end in a
// plan drawn in red on the map and its form, with Apply and Cancel, inside a
// closed pod (2026-10-06). One floating card at a time: whichever opens
// closes the one before.
let closeFloat: (() => void) | null = null;
function takeFloat(close: () => void): void {
  if (closeFloat && closeFloat !== close) closeFloat();
  closeFloat = close;
}
function releaseFloat(close: () => void): void {
  if (closeFloat === close) closeFloat = null;
}
function floatHost(): HTMLElement {
  return document.querySelector<HTMLElement>('.cockpit') ?? document.body;
}
/** The loaded entity list, registered by the legend — a proposal preview
 *  colours who-loses-what the way the map does. */
let colorSource: (() => EntityDto[]) | null = null;

/** Start "Reassign district" from a territory's card. The Layers legend owns
 *  the form; if it has not been built yet the toast says where to go. */
export function startReassignFromCard(): void {
  if (reassignEntry) reassignEntry();
  else toast('Open Layers → Territorial control → Reassign district.');
}

/** Start "Split territory" (the cut) from a territory's card. */
export function startSplitFromCard(entity: EntityDto): void {
  if (splitEntry) splitEntry(entity);
  else toast('Open Layers → Territorial control → Split territory.');
}

/** Start "Mark area" from a territory's card. */
export function startAreaFromCard(entity: EntityDto): void {
  if (areaEntry) areaEntry(entity);
  else toast('Open Layers → Territorial control → Mark area.');
}

/** Start "Add ground" from a territory's card: draw around ground and it
 *  joins THAT territory (its faction, status and contester). */
export function startClaimFromCard(entity: SlotPreset): void {
  if (claimEntry) claimEntry(entity);
  else toast('Open Layers → Territorial control → Claim area.');
}

/** Start "Captured by…" (resolve the whole territory) from its card. */
export function startResolveFromCard(entity: EntityDto): void {
  if (resolveEntry) resolveEntry(entity);
  else toast('Open Layers → Territorial control, then click the territory again.');
}

/** What a paint run applies to every district clicked. */
interface Brush { faction: string; status: string; contestedBy: string | null }

function esc(v: unknown): string {
  return String(v ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function boundsOf(geometry: unknown): maplibregl.LngLatBounds | null {
  let bounds: maplibregl.LngLatBounds | null = null;
  const walk = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      const ll: [number, number] = [c[0], c[1]];
      bounds = bounds ? bounds.extend(ll) : new maplibregl.LngLatBounds(ll, ll);
      return;
    }
    for (const part of c) walk(part);
  };
  walk((geometry as { coordinates?: unknown } | null)?.coordinates);
  return bounds;
}

interface AtDto {
  district: { fid: number; name: string | null; country: string | null;
    countryIso: string; lon: number; lat: number } | null;
  holder: { id: string; name: string | null; faction: string | null;
    status: string | null; contestedBy: string | null; asOf: string | null;
    source: string | null } | null;
}

interface CountryDto { iso: string; name: string; tracked: boolean }

/** POST /api/territory/area with dryRun: what marking the area would do. */
interface AreaPlanDto {
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  /** outsideKm2: drawn past the country; unheldKm2: on ground nobody's
   *  control is tracked on — both left out, since only held ground moves. */
  km2: number; outsideKm2: number; unheldKm2: number; absorbedKm2: number;
  countryIso: string; country: string;
  fit: { level: number; units: number; source: string } | null;
  takes: AreaTake[];
}

/** GET /api/boundaries/detail: what full-resolution boundaries are loaded. */
interface DetailCountryDto {
  iso: string; country: string | null;
  levels: { level: number; status: 'loading' | 'ready' | 'error'; units: number | null;
            error: string | null }[];
}

export interface LegendCtx {
  map: maplibregl.Map;
  me: Me;
  /** The loaded entity list — territories AND factions come from it. */
  all: () => EntityDto[];
  /** After a write: refetch the panes (tiles are bumped here). */
  onChanged: () => void;
}

/** The legend block under the "Territorial control" category row: one row
 *  per territory (swatch, holder, status, as-of), a colour picker per faction
 *  for curators, and the two curation flows. `refresh()` re-renders the rows
 *  from the current entity list; the form area survives a refresh. */
export function legendBlock(ctx: LegendCtx): { el: HTMLElement; refresh: () => void } {
  const el = document.createElement('div');
  el.className = 'ovl-sub terr-legend';
  const rows = document.createElement('div');
  rows.className = 'terr-rows';
  const acts = document.createElement('div');
  acts.className = 'terr-acts';
  // The form floats over the map (see takeFloat), not in the legend.
  const form = document.createElement('div');
  form.className = 'terr-form terr-float';
  form.setAttribute('role', 'dialog');
  form.setAttribute('aria-label', 'Territorial control');
  form.hidden = true;
  el.append(rows, acts);
  floatHost().appendChild(form);
  colorSource = ctx.all;
  // The split and area forms hold the edit lock (editlock.ts) while they
  // show. Every way of taking the form down goes through here, so a flow
  // that replaces another's form (Reassign over an open split preview, say)
  // releases the lock the other flow took — a blanked form with a live lock
  // was an invisible, permanent "finish your edit first".
  let formLock: (() => void) | null = null;
  const dismissForm = (): void => {
    formLock?.();
    formLock = null;
    form.hidden = true;
    form.innerHTML = '';
    releaseFloat(yieldFloat);
  };
  // Another floating card opening (a proposal's preview) ends whatever flow
  // this form is in — its map pick and its red plan included.
  function yieldFloat(): void {
    cleanup?.();
    brush = null;
    clearPieces?.();
    clearPieces = null;
    clearAreaPreview?.();
    clearAreaPreview = null;
    dismissForm();
  }
  /** A flow is about to show its form: take the floating slot. */
  const showFloat = (): void => {
    if (!form.isConnected) floatHost().appendChild(form);
    takeFloat(yieldFloat);
  };
  const setFormBusy = (busy: boolean): void => {
    form.querySelectorAll<HTMLInputElement>('button, input, select, textarea')
      .forEach(control => { control.disabled = busy; });
  };

  const factionNames = (): string[] => {
    const names = new Set<string>();
    for (const e of ctx.all()) {
      if (e.type === 'faction' && e.name) names.add(e.name);
      if (e.type === 'territory') {
        const f = String(e.attrs.faction ?? '').trim();
        if (f) names.add(f);
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  };
  const datalist = (): string =>
    `<datalist id="terr-factions">${factionNames().map((n) =>
      `<option value="${esc(n)}"></option>`).join('')}</datalist>`;
  const canColour = Boolean(ctx.me.isAdmin || ctx.me.isOperator);
  const pinnedKey = (name: string): string | null => {
    const k = name.trim().toLowerCase();
    return Object.keys(colorConfig.factions).find((n) => n.toLowerCase() === k) ?? null;
  };

  // The contested look: one row under the holders, since it applies to every
  // contested territory at once. Built once; refresh() restyles its swatch.
  const contestedRow = document.createElement('div');
  contestedRow.className = 'terr-row terr-contested';
  contestedRow.title = 'How contested ground is hatched and dash-outlined';
  const HATCH_WORDS: Record<ContestedStyle['hatch'], string> = {
    holder: "hatched in the holder's colour",
    contester: "hatched in the contester's colour",
    fixed: 'hatched in one colour',
  };
  function renderContestedRow(): void {
    const c = colorConfig.contested;
    const swatchColor = c.hatch === 'fixed' ? c.color : '#8a6d3b';
    contestedRow.innerHTML = `
      <span class="sw hatch" style="--c:${esc(swatchColor)};background:${esc(swatchColor)}"></span>
      <span class="lbl"><b>Contested ground</b><small>${esc(HATCH_WORDS[c.hatch])}</small></span>
      ${canColour ? `
        <select class="terr-hatch" title="Whose colour marks contested ground">
          <option value="holder"${c.hatch === 'holder' ? ' selected' : ''}>holder's colour</option>
          <option value="contester"${c.hatch === 'contester' ? ' selected' : ''}>contester's colour</option>
          <option value="fixed"${c.hatch === 'fixed' ? ' selected' : ''}>one colour</option>
        </select>
        <input type="color" class="terr-sw terr-contested-sw" value="${esc(c.color)}"
          title="The colour contested ground is hatched in"${c.hatch === 'fixed' ? '' : ' hidden'}>` : ''}`;
    if (!canColour) return;
    const sel = contestedRow.querySelector<HTMLSelectElement>('.terr-hatch')!;
    const input = contestedRow.querySelector<HTMLInputElement>('.terr-contested-sw')!;
    for (const el of [sel, input]) el.addEventListener('click', (ev) => ev.stopPropagation());
    sel.addEventListener('change', () => {
      const hatch = sel.value as ContestedStyle['hatch'];
      void saveTerritoryColors({ contested: { hatch } }).then(() => {
        applyFactionColors(ctx.map, ctx.all());
        refresh();
      }).catch((err) => toast(err instanceof Error ? err.message : 'Could not save the contested style.'));
    });
    input.addEventListener('change', () => {
      void saveTerritoryColors({ contested: { color: input.value } }).then(() => {
        applyFactionColors(ctx.map, ctx.all());
        refresh();
      }).catch((err) => toast(err instanceof Error ? err.message : 'Could not save the colour.'));
    });
  }

  function refresh(): void {
    const terrs = ctx.all().filter((e) => e.type === 'territory')
      .sort((a, b) => String(a.attrs.country ?? '').localeCompare(String(b.attrs.country ?? ''))
        || String(a.attrs.status ?? '').localeCompare(String(b.attrs.status ?? ''))
        || String(a.attrs.faction ?? '').localeCompare(String(b.attrs.faction ?? '')));
    const colors = factionColors(ctx.all());
    if (!terrs.length) {
      rows.innerHTML = `<small class="s">No country is tracked yet. Track one below, or
        import a control layer (scripts/import_territory.py).</small>`;
      return;
    }
    // One row per FACTION, its territories under it. A faction holds one
    // territory per (country, status[, contested_by]) — "controls Yemen" and
    // "contested by X in Yemen" are two rows of ONE holder — and the colour is
    // the holder's, so the picker sits on the faction, once.
    const byFaction = new Map<string, { name: string; terrs: EntityDto[] }>();
    for (const t of terrs) {
      const name = String(t.attrs.faction ?? '').trim();
      const k = name.toLowerCase();
      const g = byFaction.get(k) ?? { name, terrs: [] };
      g.terrs.push(t);
      byFaction.set(k, g);
    }
    rows.innerHTML = [...byFaction.values()].map(({ name: faction, terrs: ts }) => {
      const color = colors[faction] ?? '#8a6d3b';
      const pinned = pinnedKey(faction);
      // The swatch IS the picker for anyone who may write the setting; a
      // pinned colour gets a ↺ back to the palette.
      const swatch = canColour
        ? `<input type="color" class="sw terr-sw" value="${esc(color)}"
             data-name="${esc(faction)}" title="Colour for ${esc(faction)} — every territory it holds, saved for everyone">${
             pinned ? `<button type="button" class="terr-unpin" data-name="${esc(pinned)}"
               title="Back to the palette colour">↺</button>` : ''}`
        : `<span class="sw" style="--c:${esc(color)};background:${esc(color)}"></span>`;
      const subs = ts.map((t) => {
        const status = String(t.attrs.status ?? 'controlled');
        const by = String(t.attrs.contested_by ?? '');
        return `<div class="terr-row terr-sub" data-id="${esc(t.id)}" title="Click to zoom to it">
          <span class="sw${status === 'contested' ? ' hatch' : ''}"
            style="--c:${esc(color)};background:${esc(color)}"></span>
          <span class="lbl"><small>${status === 'contested' ? `contested${by ? ` by ${esc(by)}` : ''}` : 'controls'}
            · ${esc(String(t.attrs.country ?? t.attrs.country_iso ?? ''))}${
            t.attrs.as_of ? ` · as of ${esc(String(t.attrs.as_of))}` : ''}</small></span>
        </div>`;
      }).join('');
      return `<div class="terr-row terr-holder" title="${esc(faction)}">
        ${swatch}
        <span class="lbl"><b>${esc(faction)}</b></span>
      </div>${subs}`;
    }).join('');
    renderContestedRow();
    rows.appendChild(contestedRow);
    rows.querySelectorAll<HTMLElement>('.terr-row.terr-sub').forEach((row) => {
      row.addEventListener('click', (ev) => {
        if ((ev.target as HTMLElement).closest('input')) return;
        const t = ctx.all().find((e) => e.id === row.dataset.id);
        const b = t && boundsOf(t.geometry);
        if (b) ctx.map.fitBounds(b, { padding: 40, maxZoom: 9, duration: 600 });
      });
    });
    rows.querySelectorAll<HTMLInputElement>('.terr-row.terr-holder .terr-sw').forEach((input) => {
      input.addEventListener('click', (ev) => ev.stopPropagation());
      input.addEventListener('change', async () => {
        const name = input.dataset.name ?? '';
        if (!name) return;
        // One entry per name, whatever the case it was pinned under before.
        const factions = { ...colorConfig.factions };
        const prior = pinnedKey(name);
        if (prior) delete factions[prior];
        factions[name] = input.value;
        try {
          await saveTerritoryColors({ factions });
          applyFactionColors(ctx.map, ctx.all());
          refresh();
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not save the colour.');
        }
      });
    });
    rows.querySelectorAll<HTMLButtonElement>('.terr-unpin').forEach((btn) => {
      btn.addEventListener('click', async (ev) => {
        ev.stopPropagation();
        const factions = { ...colorConfig.factions };
        delete factions[btn.dataset.name ?? ''];
        try {
          await saveTerritoryColors({ factions });
          applyFactionColors(ctx.map, ctx.all());
          refresh();
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not reset the colour.');
        }
      });
    });
  }

  if (ctx.me.isAdmin) {
    acts.innerHTML = `
      <button type="button" class="mini terr-lead" data-a="claim"
        title="Draw a line around ground, then pick who holds it — it merges into that faction's territory">Claim area<small>Draw around ground, give it to a faction</small></button>
      <button type="button" class="mini" data-a="reassign"
        title="Click a district on the map and say who holds it now">Reassign district</button>
      <button type="button" class="mini" data-a="area"
        title="Draw, paint or pick admin units to mark ground as contested — corners snap to boundaries and roads">Mark contested area<small>Outline or paint disputed ground</small></button>
      <button type="button" class="mini" data-a="cut"
        title="Draw a line across a territory; one side of it changes hands">Split territory<small>Draw a dividing line, then assign a piece</small></button>
      <button type="button" class="mini" data-a="drag"
        title="Drag the vertices of the boundaries in view; a shared line moves on both sides">Reshape boundaries<small>Move an existing border's vertices</small></button>
      <button type="button" class="mini" data-a="track"
        title="Start tracking a country as one territory held by its government">Track a country</button>
      <button type="button" class="mini" data-a="detail"
        title="Fetch full-resolution admin boundaries (geoBoundaries) for a country — what the boundary tools snap and fit to">Detailed boundaries</button>`;
    acts.querySelector('[data-a="reassign"]')!.addEventListener('click', () => {
      startReassign();
    });
    acts.querySelector('[data-a="claim"]')!.addEventListener('click', () => {
      void startArea(null, 'claim');
    });
    acts.querySelector('[data-a="area"]')!.addEventListener('click', () => {
      void startArea(null);
    });
    acts.querySelector('[data-a="detail"]')!.addEventListener('click', () => {
      void detailForm();
    });
    acts.querySelector('[data-a="drag"]')!.addEventListener('click', () => {
      const t = territoryInView();
      if (!t) { toast('No tracked territory in view — pan to one first.'); return; }
      cleanup?.();
      dismissForm();
      editTerritoryBoundary(ctx.map, t, { onSaved: () => ctx.onChanged(), onDone: () => {} });
    });
    acts.querySelector('[data-a="cut"]')!.addEventListener('click', () => {
      void startSplit(null);
    });
    acts.querySelector('[data-a="track"]')!.addEventListener('click', () => {
      void trackForm();
    });
  }

  /** The territory under the map's centre, else the one nearest it — which
   *  country's boundaries "Reshape boundaries" opens. */
  function territoryInView(): EntityDto | null {
    const terrs = ctx.all().filter((e) => e.type === 'territory' && e.geometry);
    if (!terrs.length) return null;
    const c = ctx.map.getCenter();
    const centre: Pt = [c.lng, c.lat];
    const under = territoryUnderLine(terrs, [centre, centre]);
    if (under) return under;
    let best: EntityDto | null = null;
    let bestD = Infinity;
    for (const t of terrs) {
      const b = boundsOf(t.geometry);
      if (!b) continue;
      const m = b.getCenter();
      const d = Math.hypot(m.lng - centre[0], m.lat - centre[1]);
      if (d < bestD) { bestD = d; best = t; }
    }
    return best;
  }

  // One click reads the district and opens the form; with a BRUSH armed
  // ("keep painting"), every click applies the brush to the district under
  // it at once and re-arms, until Esc — a run of districts is a run of
  // clicks, which is how a control map is actually corrected after a
  // report of a front moving.
  let cleanup: (() => void) | null = null;
  let brush: Brush | null = null;
  reassignEntry = () => startReassign();

  async function applyTo(d: NonNullable<AtDto['district']>, choice: Brush,
                         note: string | null): Promise<boolean> {
    try {
      const res = await post<{ rev: number | null; faction: string; status: string }>(
        '/api/territory/change', {
          adm2Fid: d.fid, faction: choice.faction, status: choice.status,
          contestedBy: choice.contestedBy, note,
        });
      if (res.rev) bumpTiles(ctx.map, res.rev); else forceTileRefresh(ctx.map);
      toast(`${d.name ?? 'District'}: ${res.faction} ${res.status}${
        choice.status === 'contested' && choice.contestedBy ? ` by ${choice.contestedBy}` : ''}.`);
      ctx.onChanged();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not reassign.');
      return false;
    }
  }

  function startReassign(): void {
    cleanup?.();
    clearAreaPreview?.();
    clearAreaPreview = null;
    toast(brush
      ? `Painting ${brush.faction} (${brush.status}) — click districts, Esc stops.`
      : 'Click a district on the map — Esc cancels.');
    pickArmed = true;
    ctx.map.getCanvas().style.cursor = 'crosshair';
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      brush = null;
      cleanup?.();
      dismissForm();
      toast('Stopped.');
    };
    const onClick = (e: maplibregl.MapMouseEvent) => {
      cleanup?.();
      void (async () => {
        try {
          const at = await get<AtDto>(
            `/api/territory/at?lon=${e.lngLat.lng.toFixed(6)}&lat=${e.lngLat.lat.toFixed(6)}`);
          if (!at.district) { toast('No district there.'); if (brush) startReassign(); return; }
          if (brush && at.holder) {
            // Already as painted: skip the write, keep the run going.
            const h = at.holder;
            const same = (h.faction ?? '').toLowerCase() === brush.faction.toLowerCase()
              && h.status === brush.status
              && (h.contestedBy ?? '').toLowerCase() === (brush.contestedBy ?? '').toLowerCase();
            if (!same) await applyTo(at.district, brush, null);
            else toast(`${at.district.name ?? 'District'} already ${brush.status} by ${brush.faction}.`);
            startReassign();
            return;
          }
          reassignForm(at);
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not read the district.');
          if (brush) startReassign();
        }
      })();
    };
    ctx.map.once('click', onClick);
    document.addEventListener('keydown', onKey);
    cleanup = () => {
      ctx.map.off('click', onClick);
      document.removeEventListener('keydown', onKey);
      ctx.map.getCanvas().style.cursor = '';
      pickArmed = false;
      cleanup = null;
    };
  }

  function reassignForm(at: AtDto): void {
    const d = at.district!;
    const h = at.holder;
    showFloat();
    form.hidden = false;
    if (!h) {
      form.innerHTML = `
        <div class="terr-head"><b>${esc(d.name ?? 'District')}</b>
          <small>${esc(d.country ?? d.countryIso)}</small></div>
        <small class="s">Nobody tracks control here yet. Track ${esc(d.country ?? d.countryIso)}
          first, then reassign its districts.</small>
        <div class="terr-btns">
          <button type="button" class="mini" data-a="track">Track ${esc(d.country ?? d.countryIso)}</button>
          <button type="button" class="mini ghost" data-a="cancel">Cancel</button>
        </div>`;
      form.querySelector('[data-a="track"]')!.addEventListener('click', () => {
        void trackForm(d.countryIso);
      });
      form.querySelector('[data-a="cancel"]')!.addEventListener('click', () => {
        dismissForm();
      });
      return;
    }
    const held = `${esc(h.faction ?? '?')} (${esc(h.status ?? 'controlled')}${
      h.contestedBy ? `, contested by ${esc(h.contestedBy)}` : ''})`;
    form.innerHTML = `
      <div class="terr-head"><b>${esc(d.name ?? 'District')}</b>
        <small>${esc(d.country ?? d.countryIso)} · held by ${held}${
        h.asOf ? ` · as of ${esc(h.asOf)}` : ''}</small></div>
      <label>Held by <input list="terr-factions" data-f="faction"
        value="${esc(h.faction ?? '')}" placeholder="faction"></label>
      <label>Status <select data-f="status">
        <option value="controlled"${h.status !== 'contested' ? ' selected' : ''}>controlled</option>
        <option value="contested"${h.status === 'contested' ? ' selected' : ''}>contested</option>
      </select></label>
      <label>Contested by <input list="terr-factions" data-f="by"
        value="${esc(h.contestedBy ?? '')}" placeholder="only when contested"></label>
      <label>Note <input data-f="note" placeholder="why (optional)"></label>
      <label class="terr-keep"><input type="checkbox" data-f="keep">
        <span>Keep painting <small>apply the same to every district I click next, until Esc</small></span></label>
      ${datalist()}
      <div class="terr-btns">
        <button type="button" class="mini commit" data-a="apply">Apply</button>
        <button type="button" class="mini ghost" data-a="cancel">Cancel</button>
      </div>`;
    const field = (k: string) => form.querySelector(`[data-f="${k}"]`) as HTMLInputElement;
    form.querySelector('[data-a="cancel"]')!.addEventListener('click', () => {
      brush = null;
      dismissForm();
    });
    form.querySelector('[data-a="apply"]')!.addEventListener('click', async () => {
      const button = form.querySelector('[data-a="apply"]') as HTMLButtonElement;
      button.disabled = true;
      const choice: Brush = {
        faction: field('faction').value.trim(),
        status: field('status').value,
        contestedBy: field('by').value.trim() || null,
      };
      const ok = await applyTo(d, choice, field('note').value.trim() || null);
      if (!ok) { button.disabled = false; return; }
      if (field('keep').checked) {
        brush = choice;
        form.innerHTML = `<div class="terr-head"><b>Painting ${esc(choice.faction)}
          (${esc(choice.status)}${choice.contestedBy ? `, contested by ${esc(choice.contestedBy)}` : ''})</b>
          <small>Click districts on the map; each one is reassigned at once. Esc stops.</small></div>
          <div class="terr-btns"><button type="button" class="mini ghost" data-a="stop">Stop</button></div>`;
        form.querySelector('[data-a="stop"]')!.addEventListener('click', () => {
          brush = null; cleanup?.(); dismissForm();
        });
        startReassign();
        return;
      }
      dismissForm();
    });
  }

  // --- the cut: draw a line, pick a piece, name the holder ------------------
  // A line drawn from outside a territory to outside it is a new boundary.
  // The server cuts the territory and answers with the pieces and the
  // neighbour across each; the operator picks the piece that changes hands
  // (on the map or in the list) and confirms who gets it. Nothing is written
  // until Apply, and the apply re-cuts the same line server-side.
  let clearPieces: (() => void) | null = null;
  let clearAreaPreview: (() => void) | null = null;
  splitEntry = (entity) => { void startSplit(entity); };
  areaEntry = (entity) => { void startArea(entity); };
  claimEntry = (entity) => { void startArea(entity, 'claim'); };
  resolveEntry = (entity) => { startResolve(entity); };

  // Contest → capture. The territory is the unit: "this contested ground is
  // now X's" reassigns its whole outline (POST /api/territory/resolve), so
  // it joins X's territory in the country and the contested row is gone.
  // The same form re-labels a controlled territory as contested, or hands
  // it over whole. The holder defaults to the CONTESTER — the usual answer.
  function startResolve(entity: EntityDto): void {
    cleanup?.();
    clearAreaPreview?.();
    clearAreaPreview = null;
    clearPieces?.();
    clearPieces = null;
    showFloat();
    const faction = String(entity.attrs.faction ?? '');
    const status = String(entity.attrs.status ?? 'controlled');
    const by = String(entity.attrs.contested_by ?? '');
    const country = String(entity.attrs.country ?? entity.attrs.country_iso ?? '');
    const contested = status === 'contested';
    form.hidden = false;
    form.innerHTML = `
      <div class="terr-head"><b>${contested ? 'Contested ground captured' : 'Whole territory'}</b>
        <small>${esc(faction)}${contested ? ` · contested by ${esc(by || '?')}` : ' · controls'} · ${esc(country)}.
          Say who holds all of it now; it joins that holder's ground.</small></div>
      <label>Held by <input list="terr-factions" data-f="faction" placeholder="faction"
        value="${esc(contested && by ? by : faction)}"></label>
      <label>Status <select data-f="status">
        <option value="controlled" selected>controlled</option>
        <option value="contested">contested</option>
      </select></label>
      <label hidden>Contested by <input list="terr-factions" data-f="by" placeholder="only when contested"></label>
      <label>Note <input data-f="note" placeholder="why (optional)"></label>
      ${datalist()}
      <div class="terr-btns">
        <button type="button" class="mini commit" data-a="apply">${contested ? 'Mark as captured' : 'Apply'}</button>
        <button type="button" class="mini ghost" data-a="cancel">Cancel</button>
      </div>`;
    const field = (k: string) => form.querySelector(`[data-f="${k}"]`) as HTMLInputElement;
    const close = (): void => { dismissForm(); };
    field('status').addEventListener('change', () => {
      const c = field('status').value === 'contested';
      field('by').closest('label')!.hidden = !c;
      form.querySelector('[data-a="apply"]')!.textContent = c ? 'Mark as contested' : 'Mark as captured';
    });
    form.querySelector('[data-a="cancel"]')!.addEventListener('click', close);
    form.querySelector('[data-a="apply"]')!.addEventListener('click', async () => {
      const button = form.querySelector('[data-a="apply"]') as HTMLButtonElement;
      const holder = field('faction').value.trim();
      if (!holder) { toast('Name the faction that holds it now.'); return; }
      setFormBusy(true);
      button.textContent = 'Saving…';
      try {
        const res = await post<{ rev: number | null; faction: string; status: string;
                                 contestedBy: string | null }>(
          '/api/territory/resolve', {
            id: entity.id, faction: holder,
            status: field('status').value,
            contestedBy: field('by').value.trim() || null,
            note: field('note').value.trim() || null,
          });
        if (res.rev) bumpTiles(ctx.map, res.rev); else forceTileRefresh(ctx.map);
        toast(res.status === 'contested'
          ? `${esc(faction)}'s ground in ${country} is now contested by ${res.contestedBy ?? '?'}.`
          : `${res.faction} now controls it${contested ? ` — captured from ${faction}` : ''}.`);
        close();
        ctx.onChanged();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not resolve the territory.');
        setFormBusy(false);
        button.textContent = contested ? 'Mark as captured' : 'Apply';
      }
    });
    field('faction').focus();
  }

  async function startSplit(preset: EntityDto | null): Promise<void> {
    cleanup?.();
    clearAreaPreview?.();
    clearAreaPreview = null;
    clearPieces?.();
    dismissForm();
    toast(preset
      ? `Draw the new boundary across ${preset.attrs.faction ?? 'the territory'} — from outside one edge to outside the other.`
      : 'Draw the new boundary across a territory, from outside one edge to outside the other.');
    const terrs = ctx.all().filter((e) => e.type === 'territory' && e.geometry);
    const line = await captureLine(ctx.map, 'Split territory', {
      iso: String(preset?.attrs.country_iso ?? ''), outlines: terrs,
    });
    if (!line) return;
    const target = preset && territoryUnderLine([preset], line) ? preset
      : territoryUnderLine(terrs, line);
    if (!target) { toast('The line crosses no tracked territory.'); return; }
    showFloat();
    form.hidden = false;
    let previewClosed = false;
    let previewSaving = false;
    const releasePreview = beginMapEdit({ surface: ctx.map.getCanvas(), controls: form, cancel: () => closePreview() });
    formLock = releasePreview;
    const closePreview = (): void => {
      if (previewSaving) return;
      previewClosed = true; releasePreview();
      clearPieces?.(); clearPieces = null;
      dismissForm();
    };
    form.innerHTML = '<small class="s">Preparing the split…</small><button type="button" class="mini" data-a="cancel">Cancel</button>';
    form.querySelector('[data-a="cancel"]')!.addEventListener('click', closePreview);
    let preview: { id: string; faction: string | null; status: string | null; pieces: PieceDto[] };
    try {
      preview = await post('/api/territory/split', { id: target.id, line });
    } catch (err) {
      if (previewClosed) return;
      form.innerHTML = `<small class="s">${esc(err instanceof Error ? err.message : 'Could not cut it.')}</small>
        <div class="terr-btns"><button type="button" class="mini ghost" data-a="cancel">Close</button></div>`;
      form.querySelector('[data-a="cancel"]')!.addEventListener('click', closePreview);
      return;
    }
    if (previewClosed) return;
    // Default to the piece that borders someone: that is the one a front
    // moving hands over; the largest piece is usually the one that stays.
    const pieces = preview.pieces;
    let picked = pieces.find((p) => p.neighbour)?.index ?? pieces[pieces.length - 1]!.index;
    const show = (): void => {
      clearPieces?.();
      clearPieces = showPieces(ctx.map, line, pieces, picked, (index) => {
        picked = index;
        paintPick();
      });
    };
    show();
    const pieceRow = (p: PieceDto): string => `
      <label class="terr-piece" data-piece="${p.index}">
        <input type="radio" name="terr-piece" value="${p.index}"${p.index === picked ? ' checked' : ''}>
        <span><b>Piece ${p.index + 1}</b> · ${esc(fmtKm2(p.km2))}
          <small>${p.neighbour ? `borders ${esc(p.neighbour.faction ?? p.neighbour.name ?? '?')}` : 'borders no other territory'}</small></span>
      </label>`;
    form.innerHTML = `
      <div class="terr-head"><b>Cutting ${esc(preview.faction ?? target.name ?? 'territory')}</b>
        <small>${pieces.length} pieces. Pick the one that changes hands — on the map or below — and say who holds it now.</small></div>
      <div class="terr-pieces">${pieces.map(pieceRow).join('')}</div>
      <label>Held by <input list="terr-factions" data-f="faction" placeholder="faction"></label>
      <label>Status <select data-f="status">
        <option value="controlled">controlled</option>
        <option value="contested">contested</option>
      </select></label>
      <label>Contested by <input list="terr-factions" data-f="by" placeholder="only when contested"></label>
      <label>Note <input data-f="note" placeholder="why (optional)"></label>
      ${datalist()}
      <div class="terr-btns">
        <button type="button" class="mini commit" data-a="apply">Apply</button>
        <button type="button" class="mini ghost" data-a="cancel">Cancel</button>
      </div>`;
    const field = (k: string) => form.querySelector(`[data-f="${k}"]`) as HTMLInputElement;
    // The holder fields follow the picked piece: the neighbour across it,
    // else the territory's own holder (a cut that carves a contested zone).
    function paintPick(): void {
      form.querySelectorAll<HTMLInputElement>('input[name="terr-piece"]').forEach((r) => {
        r.checked = Number(r.value) === picked;
      });
      const p = pieces.find((x) => x.index === picked);
      const nb = p?.neighbour;
      field('faction').value = nb?.faction ?? preview.faction ?? '';
      field('status').value = nb?.status === 'contested' ? 'contested' : 'controlled';
      field('by').value = nb?.contestedBy ?? '';
    }
    paintPick();
    form.querySelectorAll<HTMLInputElement>('input[name="terr-piece"]').forEach((r) => {
      r.addEventListener('change', () => {
        picked = Number(r.value);
        show();
        paintPick();
      });
    });
    const close = closePreview;
    form.querySelector('[data-a="cancel"]')!.addEventListener('click', close);
    form.querySelector('[data-a="apply"]')!.addEventListener('click', async () => {
      const button = form.querySelector('[data-a="apply"]') as HTMLButtonElement;
      const faction = field('faction').value.trim();
      if (!faction) { toast('Name the faction the piece goes to.'); return; }
      previewSaving = true;
      setFormBusy(true);
      button.textContent = 'Saving…';
      try {
        const res = await post<{ rev: number | null; faction: string; status: string; km2: number;
                                 created: string | null }>(
          '/api/territory/split', {
            id: target.id, line, piece: picked, faction,
            status: field('status').value,
            contestedBy: field('by').value.trim() || null,
            note: field('note').value.trim() || null,
          });
        if (res.rev) bumpTiles(ctx.map, res.rev); else forceTileRefresh(ctx.map);
        toast(`${fmtKm2(res.km2)} now ${res.status} by ${res.faction}.`);
        previewSaving = false;
        close();
        ctx.onChanged();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not apply the cut.');
        previewSaving = false; setFormBusy(false);
        button.textContent = 'Apply';
      }
    });
  }

  // --- marking an area: draw around it, then say whose it is ----------------
  // The gesture is territoryarea.ts. The server plans the change — clipped to
  // one country and to the ground somebody already holds there (an area only
  // RE-DIVIDES tracked territory; drawing loosely past the front or out to sea
  // costs nothing), fitted to units if asked, grown over the slivers along its
  // edge — and the form shows that plan, drawn on the map, before anything is
  // written. Apply sends the same inputs again and the server re-plans them.
  //
  // Two doors, one form. CLAIM ("this ground is X's now") opens on
  // controlled and guesses nobody: the receiver is a click on a chip, and the
  // area MERGES into X's territory in the country — reassign_area unions into
  // the slot — so the outline only has to be right on the new side; whatever
  // overlaps X's own ground is already X's. From a territory's card the slot
  // is preset, so it is draw, then Apply. CONTEST opens on contested with the
  // biggest loser as holder and the other side as contester, as before.

  type AreaIntent = 'claim' | 'contest';
  interface AreaChoice { faction: string; status: string; by: string; note: string }

  async function startArea(preset: SlotPreset | null, intent: AreaIntent = 'contest'): Promise<void> {
    showFloat();
    cleanup?.();
    clearPieces?.();
    clearPieces = null;
    clearAreaPreview?.();
    clearAreaPreview = null;
    dismissForm();
    const anchor = preset ?? territoryInView();
    const iso = String(anchor?.attrs.country_iso ?? '').toUpperCase();
    const claim = intent === 'claim';
    const slot: AreaChoice | undefined = claim && preset ? {
      faction: String(preset.attrs.faction ?? ''),
      status: String(preset.attrs.status ?? 'controlled'),
      by: String(preset.attrs.contested_by ?? ''),
      note: '',
    } : undefined;
    const who = slot?.faction ? `${slot.faction}'s` : 'the new holder’s';
    const input = await captureTerritoryArea(ctx.map, {
      title: claim ? (slot?.faction ? `Add ground to ${slot.faction}` : 'Claim area') : 'Mark contested area',
      iso,
      mode: 'lasso',
      hint: claim
        ? `Draw around the ground that changed hands. Only ground somebody already holds moves, so drawing past the front or over the sea is fine, and overlapping ${who} own ground just merges.`
        : 'Draw around the contested ground. Only ground somebody already holds is marked.',
      outlines: outlinesIn(iso),
    });
    if (!input) return;
    showFloat();
    await areaForm(input, { intent, carry: slot, redraw: () => { void startArea(preset, intent); } });
  }

  function outlinesIn(iso: string): EntityDto[] {
    return ctx.all().filter((e) => e.type === 'territory' && e.geometry
      && (!iso || String(e.attrs.country_iso ?? '').toUpperCase() === iso));
  }

  async function areaForm(input: AreaInput,
                          opts: { intent: AreaIntent; carry?: AreaChoice; redraw: () => void }):
  Promise<void> {
    const claim = opts.intent === 'claim';
    let closed = false;
    let saving = false;
    const releasePreview = beginMapEdit({ surface: ctx.map.getCanvas(), controls: form, cancel: () => close() });
    formLock = releasePreview;
    let fit = false;
    // A claim never guesses who receives the ground; a carried choice (a
    // card's slot, or the form before "Add more") is kept as it was.
    let seeded = claim || !!opts.carry;
    const kept: AreaChoice = {
      faction: opts.carry?.faction ?? '',
      status: opts.carry?.status || (claim ? 'controlled' : 'contested'),
      by: opts.carry?.by ?? '',
      note: opts.carry?.note ?? '',
    };
    const inputs = (): Record<string, unknown> => ({
      polygon: input.polygon, strokes: input.strokes, units: input.units,
      fitLevel: fit ? input.level : undefined,
    });
    const field = (k: string): HTMLInputElement | null =>
      form.querySelector(`[data-f="${k}"]`) as HTMLInputElement | null;
    const close = (): void => {
      if (saving) return;
      closed = true; releasePreview();
      clearAreaPreview?.();
      clearAreaPreview = null;
      dismissForm();
    };
    const remember = (): void => {
      if (!field('faction')) return;
      kept.faction = field('faction')!.value;
      kept.status = field('status')!.value;
      kept.by = field('by')!.value;
      kept.note = field('note')!.value;
    };
    const redraw = (): void => {
      close();
      opts.redraw();
    };
    const failed = (msg: string): void => {
      form.innerHTML = `
        <div class="terr-head"><b>${claim ? 'Claim area' : 'Mark area'}</b><small>${esc(msg)}</small></div>
        <div class="terr-btns">
          ${fit ? '<button type="button" class="mini" data-a="unfit">Don’t fit</button>' : ''}
          <button type="button" class="mini" data-a="redraw">Draw again</button>
          <button type="button" class="mini ghost" data-a="cancel">Close</button>
        </div>`;
      form.querySelector('[data-a="unfit"]')?.addEventListener('click', () => {
        fit = false;
        void replan();
      });
      form.querySelector('[data-a="redraw"]')!.addEventListener('click', redraw);
      form.querySelector('[data-a="cancel"]')!.addEventListener('click', close);
    };

    async function replan(): Promise<void> {
      remember();
      form.hidden = false;
      form.innerHTML = '<small class="s">Measuring the area…</small><button type="button" class="mini" data-a="cancel">Cancel</button>';
      form.querySelector('[data-a="cancel"]')!.addEventListener('click', close);
      let plan: AreaPlanDto;
      try {
        plan = await post<AreaPlanDto>('/api/territory/area', { ...inputs(), dryRun: true });
      } catch (err) {
        if (closed) return;
        clearAreaPreview?.();
        clearAreaPreview = null;
        failed(err instanceof Error ? err.message : 'Could not measure the area.');
        return;
      }
      if (closed) return;
      clearAreaPreview?.();
      clearAreaPreview = showAreaPreview(ctx.map, plan.geometry);
      if (!seeded) {
        const d = areaDefaults(plan.takes);
        kept.faction = d.faction;
        kept.by = d.contestedBy;
        seeded = true;
      }
      render(plan);
    }

    /** "Add more": draw again over the plan (shown in red), then plan
     *  everything together. Cancelling it comes back to this area. */
    async function addMore(p: AreaPlanDto): Promise<void> {
      remember();
      const carry = { ...kept };
      close();
      const more = await captureTerritoryArea(ctx.map, {
        title: 'Add to the area', iso: p.countryIso, mode: 'lasso', base: p.geometry,
        outlines: outlinesIn(p.countryIso),
      });
      showFloat();
      await areaForm(more ? mergeAreaInputs(input, more) : input, { ...opts, carry });
    }

    function render(p: AreaPlanDto): void {
      const colors = factionColors(ctx.all());
      const colorOf = (f: string | null): string => colors[f ?? ''] ?? '#8a6d3b';
      const notes: string[] = [];
      if (p.fit) {
        notes.push(`fitted to ${p.fit.units} ADM${p.fit.level} unit${p.fit.units === 1 ? '' : 's'}`
          + ` (${p.fit.source === 'detail' ? 'detailed' : 'CGAZ'} boundaries)`);
      }
      if (p.outsideKm2 >= 0.1) notes.push(`${fmtKm2(p.outsideKm2)} outside ${p.country} left out`);
      if (p.unheldKm2 >= 0.1) notes.push(`${fmtKm2(p.unheldKm2)} nobody holds left out`);
      if (p.absorbedKm2 >= 0.1) notes.push(`takes ${fmtKm2(p.absorbedKm2)} of slivers along its edge`);
      // The quick picks: whoever the area touches and everyone else holding
      // ground in the country, alphabetical so a faction's chip is always in
      // the same place.
      const picks = new Map<string, string>();
      const pick = (n: string | null | undefined): void => {
        const t = String(n ?? '').trim();
        if (t && !picks.has(t.toLowerCase())) picks.set(t.toLowerCase(), t);
      };
      p.takes.forEach((t) => pick(t.faction));
      outlinesIn(p.countryIso).forEach((e) => pick(String(e.attrs.faction ?? '')));
      pick(kept.faction);
      const chips = [...picks.values()].sort((a, b) => a.localeCompare(b)).map((f) => `<button type="button" class="terr-chip"
          data-faction="${esc(f)}" aria-pressed="false" title="${esc(f)}">
          <span class="sw" style="background:${esc(colorOf(f))}"></span><span class="nm">${esc(f)}</span>
          <small data-gain></small></button>`).join('');
      form.innerHTML = `
        <div class="terr-head"><b>${claim ? 'Give' : 'Mark'} ${esc(fmtKm2(p.km2))} in ${esc(p.country)}${claim ? ' to…' : ''}</b>
          <small>${esc(notes.join(' · ') || 'Outlined in red on the map.')}</small></div>
        ${chips ? `<div class="terr-chips" role="group" aria-label="Who holds it">${chips}</div>` : ''}
        <label>${claim ? 'Give to' : 'Held by'} <input list="terr-factions" data-f="faction" value="${esc(kept.faction)}" placeholder="faction"></label>
        <label>Mark this area as <select data-f="status">
          <option value="controlled"${kept.status !== 'contested' ? ' selected' : ''}>controlled</option>
          <option value="contested"${kept.status === 'contested' ? ' selected' : ''}>contested</option>
        </select></label>
        <label>Contested by <input list="terr-factions" data-f="by" value="${esc(kept.by)}" placeholder="Faction contesting this ground (optional)"></label>
        <div class="terr-takes" data-takes></div>
        <label class="terr-keep"><input type="checkbox" data-f="fit"${fit ? ' checked' : ''}>
          <span>Fit to ADM${input.level} boundaries <small>use the units the area mostly covers, so its edges are the boundaries</small></span></label>
        <label>Note <input data-f="note" value="${esc(kept.note)}" placeholder="why (optional)"></label>
        ${datalist()}
        <div class="terr-btns">
          <button type="button" class="mini commit" data-a="apply">Apply</button>
          <button type="button" class="mini" data-a="more" title="Draw around more ground and add it to this area">＋ Add more</button>
          <button type="button" class="mini" data-a="redraw">Redraw</button>
          <button type="button" class="mini ghost" data-a="cancel">Cancel</button>
        </div>`;
      const apply = form.querySelector('[data-a="apply"]') as HTMLButtonElement;
      const choice = (): { faction: string; status: string; by: string } => {
        const status = field('status')!.value;
        return { faction: field('faction')!.value.trim(), status,
                 by: status === 'contested' ? field('by')!.value.trim() : '' };
      };
      /** Everything that follows the choice: the pressed chip, what each
       *  chip would gain, what the area takes and from whom, the button. */
      const sync = (): void => {
        const c = choice();
        field('by')!.closest('label')!.hidden = c.status !== 'contested';
        form.querySelectorAll<HTMLButtonElement>('.terr-chip').forEach((b) => {
          const f = b.dataset.faction ?? '';
          b.setAttribute('aria-pressed', String(f.toLowerCase() === c.faction.toLowerCase()));
          const gain = slotGain(p.km2, p.takes, f, c.status, c.status === 'contested' ? c.by : '');
          b.querySelector('[data-gain]')!.textContent = gain < p.km2 - 0.05 ? `+${fmtKm2(gain)}` : '';
        });
        const lines = p.takes.map((t) => {
          const who = `<span class="sw" style="background:${esc(colorOf(t.faction))}"></span>${
            esc(t.faction ?? t.name ?? '?')}${t.status === 'contested'
              ? ` (contested${t.contestedBy ? ` by ${esc(t.contestedBy)}` : ''})` : ''}`;
          return c.faction && sameSlot(t, c.faction, c.status, c.by)
            ? `<div>${who}: ${esc(fmtKm2(t.km2))} already theirs — merges</div>`
            : `<div>Takes ${esc(fmtKm2(t.km2))} from ${who}</div>`;
        });
        form.querySelector('[data-takes]')!.innerHTML = lines.join('')
          || '<small class="s">Only ground somebody already holds changes hands.</small>';
        const gain = c.faction ? slotGain(p.km2, p.takes, c.faction, c.status, c.by) : p.km2;
        apply.disabled = !c.faction;
        apply.textContent = !c.faction ? 'Choose who holds it'
          : c.status === 'contested' ? `Mark ${fmtKm2(gain)} contested`
          : `Give ${fmtKm2(gain)} to ${c.faction}`;
      };
      form.querySelectorAll<HTMLButtonElement>('.terr-chip').forEach((b) => {
        b.addEventListener('click', () => {
          field('faction')!.value = b.dataset.faction ?? '';
          sync();
        });
      });
      for (const k of ['faction', 'by']) field(k)!.addEventListener('input', sync);
      field('status')!.addEventListener('change', sync);
      sync();
      field('fit')!.addEventListener('change', () => {
        fit = field('fit')!.checked;
        void replan();
      });
      form.querySelector('[data-a="more"]')!.addEventListener('click', () => { void addMore(p); });
      form.querySelector('[data-a="redraw"]')!.addEventListener('click', redraw);
      form.querySelector('[data-a="cancel"]')!.addEventListener('click', close);
      apply.addEventListener('click', async () => {
        const c = choice();
        if (!c.faction) { toast('Choose the faction that holds the area.'); return; }
        const gain = slotGain(p.km2, p.takes, c.faction, c.status, c.by);
        saving = true;
        setFormBusy(true);
        apply.textContent = 'Saving…';
        try {
          const res = await post<{ rev: number | null; faction: string; status: string;
                                   contestedBy: string | null; km2: number; created: string | null }>(
            '/api/territory/area', {
              ...inputs(), faction: c.faction, status: c.status,
              contestedBy: c.by || null,
              note: field('note')!.value.trim() || null,
            });
          if (res.rev) bumpTiles(ctx.map, res.rev); else forceTileRefresh(ctx.map);
          const what = res.status === 'contested'
            ? `contested${res.contestedBy ? ` by ${res.contestedBy}` : ''}, held by ${res.faction}`
            : `held by ${res.faction}`;
          toast(res.created
            ? `${fmtKm2(res.km2)} now ${what} — a new territory.`
            : `${fmtKm2(gain)} more ground ${what} — merged into its territory.`);
          saving = false;
          close();
          ctx.onChanged();
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not mark the area.');
          saving = false; setFormBusy(false); sync();
        }
      });
    }

    await replan();
  }

  // --- detailed boundaries: what the boundary tools snap and fit to -------
  // CGAZ is generalised (Yemen's districts ~1.9 km a segment); geoBoundaries
  // gbOpen is the full-resolution set, with ADM3/ADM4 where a country has
  // them. Loaded per country, in the background, on the server.

  async function detailForm(): Promise<void> {
    cleanup?.();
    clearPieces?.();
    clearPieces = null;
    clearAreaPreview?.();
    clearAreaPreview = null;
    showFloat();
    form.hidden = false;
    form.innerHTML = '<small class="s">Loading…</small>';
    await renderDetail();
  }

  async function renderDetail(): Promise<void> {
    let countries: DetailCountryDto[];
    try {
      countries = (await get<{ countries: DetailCountryDto[] }>('/api/boundaries/detail')).countries;
    } catch (err) {
      form.innerHTML = `<small class="s">${esc(err instanceof Error ? err.message : 'Could not list detailed boundaries.')}</small>`;
      return;
    }
    const tracked = new Set(ctx.all().filter((e) => e.type === 'territory')
      .map((e) => String(e.attrs.country_iso ?? '').toUpperCase()).filter(Boolean));
    const byIso = new Map(countries.map((c) => [c.iso, c]));
    const isos = [...new Set([...tracked, ...byIso.keys()])].sort();
    const summary = (c: DetailCountryDto | undefined): string => {
      if (!c?.levels.length) return 'not loaded — the tools use CGAZ';
      return c.levels.map((l) => (l.status === 'ready'
        ? `ADM${l.level} ✓ ${(l.units ?? 0).toLocaleString()}`
        : l.status === 'loading' ? `ADM${l.level} loading…` : `ADM${l.level} failed`)).join(' · ');
    };
    const loading = countries.some((c) => c.levels.some((l) => l.status === 'loading'));
    form.innerHTML = `
      <div class="terr-head"><b>Detailed boundaries</b>
        <small>Full-resolution admin units from geoBoundaries (gbOpen), what the boundary
          tools snap, trace and fit to. CGAZ stays the district set; these sit beside it.</small></div>
      <div class="terr-detail">${isos.map((iso) => {
        const c = byIso.get(iso);
        const busy = c?.levels.some((l) => l.status === 'loading');
        const errs = (c?.levels ?? []).filter((l) => l.status === 'error')
          .map((l) => `ADM${l.level}: ${l.error ?? 'failed'}`).join('; ');
        return `<div class="terr-drow"${errs ? ` title="${esc(errs)}"` : ''}><b>${esc(iso)}</b>
          <small>${esc(summary(c))}${tracked.has(iso) ? '' : ' · not tracked'}</small>
          <button type="button" class="mini" data-load="${esc(iso)}"${busy ? ' disabled' : ''}>${
            c?.levels.length ? 'Reload' : 'Load'}</button></div>`;
      }).join('') || '<small class="s">Nothing tracked or loaded yet.</small>'}</div>
      <label>Country <input data-f="iso" maxlength="3" placeholder="ISO3, e.g. SYR"></label>
      <div class="terr-btns">
        <button type="button" class="mini commit" data-a="load">Load</button>
        <button type="button" class="mini ghost" data-a="close">Close</button>
      </div>`;
    const load = async (iso: string): Promise<void> => {
      try {
        await post('/api/boundaries/detail', { iso });
        toast(`Fetching detailed boundaries for ${iso} — a big country takes a minute or two.`);
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not start the load.');
      }
      if (!form.hidden && form.querySelector('.terr-detail')) await renderDetail();
    };
    form.querySelectorAll<HTMLButtonElement>('[data-load]').forEach((b) => {
      b.addEventListener('click', () => {
        b.disabled = true;
        void load(b.dataset.load!);
      });
    });
    form.querySelector('[data-a="load"]')!.addEventListener('click', () => {
      const iso = (form.querySelector('[data-f="iso"]') as HTMLInputElement).value.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(iso)) { toast('Give a three-letter ISO code.'); return; }
      void load(iso);
    });
    form.querySelector('[data-a="close"]')!.addEventListener('click', () => {
      dismissForm();
    });
    if (loading) {
      // Poll while something loads — only as long as this view is still the form.
      window.setTimeout(() => {
        if (!form.hidden && form.querySelector('.terr-detail')) void renderDetail();
      }, 5000);
    }
  }

  async function trackForm(presetIso?: string): Promise<void> {
    showFloat();
    form.hidden = false;
    form.innerHTML = '<small class="s">Loading countries…</small>';
    let countries: CountryDto[] = [];
    try {
      countries = (await get<{ countries: CountryDto[] }>('/api/territory/countries')).countries;
    } catch (err) {
      form.innerHTML = `<small class="s">${esc(err instanceof Error ? err.message : 'Could not load countries.')}</small>`;
      return;
    }
    if (!countries.length) {
      form.innerHTML = `<small class="s">No admin boundaries are loaded, so there is no
        country outline to start from (scripts/load_admin_boundaries.ps1).</small>`;
      return;
    }
    form.innerHTML = `
      <div class="terr-head"><b>Track a country</b>
        <small>One territory — the whole country — held by its government. Districts
          move from there.</small></div>
      <label>Country <select data-f="country">
        ${countries.map((c) => `<option value="${esc(c.iso)}"${c.tracked ? ' disabled' : ''}${
          c.iso === presetIso ? ' selected' : ''}>${esc(c.name)}${c.tracked ? ' — tracked' : ''}</option>`).join('')}
      </select></label>
      <label>Held by <input list="terr-factions" data-f="faction" placeholder="e.g. Government of Yemen"></label>
      ${datalist()}
      <div class="terr-btns">
        <button type="button" class="mini commit" data-a="track">Track</button>
        <button type="button" class="mini ghost" data-a="cancel">Cancel</button>
      </div>`;
    form.querySelector('[data-a="cancel"]')!.addEventListener('click', () => {
      dismissForm();
    });
    form.querySelector('[data-a="track"]')!.addEventListener('click', async () => {
      const country = (form.querySelector('[data-f="country"]') as HTMLSelectElement).value;
      const faction = (form.querySelector('[data-f="faction"]') as HTMLInputElement).value.trim();
      if (!faction) { toast('Name the holding faction.'); return; }
      const button = form.querySelector('[data-a="track"]') as HTMLButtonElement;
      button.disabled = true;
      try {
        const created = await post<EntityDto>('/api/territory/track', { country, faction });
        bumpTiles(ctx.map, created.rev);
        // The boundary tools snap and fit to the detailed admin units: fetch
        // them now, in the background, rather than at the first edit.
        void post('/api/boundaries/detail', { iso: country }).catch(() => {});
        toast(`Tracking ${country}: ${faction}. Fetching its detailed boundaries in the background.`);
        dismissForm();
        ctx.onChanged();
        const b = boundsOf(created.geometry);
        if (b) ctx.map.fitBounds(b, { padding: 40, maxZoom: 7, duration: 800 });
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not track it.');
        button.disabled = false;
      }
    });
  }

  refresh();
  return { el, refresh };
}

// --- the Intel pane rows ----------------------------------------------------

/** One "control change" proposal (lane `control`): the live feed says a
 *  district changed hands or came under attack. The wording carries the maybe
 *  and the basis out loud — "3 reports … actor stated" — because a control
 *  map repainting on a single channel's claim is the failure the whole lane
 *  exists to prevent. The row offers PREVIEW and dismiss only: Apply lives on
 *  the preview, so nothing changes hands without the change having been seen
 *  on the map (2026-10-06). */
export function controlRow(s: ControlSuggestionDto, map: maplibregl.Map,
                           onDone: () => void): HTMLElement {
  const row = document.createElement('div');
  row.className = 'row sug-row';
  row.dataset.proposal = s.id;
  row.classList.toggle('on', preview?.id === s.id);
  const conf = s.confidence ? `<span class="terr-conf terr-${esc(s.confidence)}">${esc(s.confidence)}</span>` : '';
  row.innerHTML = `
    <div class="sug-main">
      <b>▦ ${esc(s.districtName ?? 'District')}${s.country ? `, ${esc(s.country)}` : ''} ${proposalWhat(s)}</b>
      <small>${proposalFrom(s)} · ${s.evidenceCount} report${s.evidenceCount === 1 ? '' : 's'} ·
        ${s.channels} channel${s.channels === 1 ? '' : 's'} · ${conf} · ${relTime(s.updatedAt)} ago</small>
      <small class="sug-why">${esc(s.why)}</small>
    </div>
    <div class="sug-acts">
      <button type="button" class="mini" data-a="preview"
        title="Show the change on the map, with the reports behind it. Apply is there.">Preview</button>
      <button type="button" class="mini ghost" data-a="dismiss" title="Not a change of control">✕</button>
    </div>`;
  row.querySelector('[data-a="preview"]')!.addEventListener('click', () => {
    void previewProposal(s, map, onDone);
  });
  row.querySelector('[data-a="dismiss"]')!.addEventListener('click', async () => {
    await dismissAdvice('control', s.id);
    if (preview?.id === s.id) preview.close();
    onDone();
  });
  return row;
}

export function controlRows(map: maplibregl.Map, onDone: () => void): HTMLElement[] {
  return controlSuggestions().map((s) => controlRow(s, map, onDone));
}

function proposalWhat(s: ControlSuggestionDto): string {
  return s.toStatus === 'contested'
    ? `contested by ${esc(s.contestedBy ?? '?')}`
    : `→ ${esc(s.toFaction)}`;
}

function proposalFrom(s: ControlSuggestionDto): string {
  return s.fromFaction ? `held by ${esc(s.fromFaction)} (${esc(s.fromStatus ?? '')})` : 'holder unknown';
}

/** The open proposal preview, if any — one at a time, in the floating slot. */
let preview: { id: string; close: () => void } | null = null;

/** Show what applying a proposal would do: the planned outline drawn in red
 *  (the same red as a claim's plan), who loses how much, and the reports
 *  behind it, each a click from its spot on the map. Apply, Dismiss, or "Draw
 *  it instead" — a capture of a hill is not a capture of its district, and
 *  the curator can claim just the ground that moved. */
async function previewProposal(s: ControlSuggestionDto, map: maplibregl.Map,
                               onDone: () => void): Promise<void> {
  preview?.close();
  const card = document.createElement('div');
  card.className = 'terr-form terr-float terr-preview';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', 'Proposed change of control');
  const head = `<div class="terr-head"><b>▦ ${esc(s.districtName ?? 'District')}${
    s.country ? `, ${esc(s.country)}` : ''} ${proposalWhat(s)}</b>
    <small>${proposalFrom(s)} · proposed from live reports, not applied</small></div>`;
  card.innerHTML = `${head}<small class="s">Planning the change…</small>
    <div class="terr-btns"><button type="button" class="mini ghost" data-a="close">Close</button></div>`;
  floatHost().appendChild(card);
  let clearArea: (() => void) | null = null;
  let closed = false;
  const markRows = (): void => {
    document.querySelectorAll<HTMLElement>('.sug-row[data-proposal]').forEach((r) => {
      r.classList.toggle('on', preview?.id === r.dataset.proposal);
    });
  };
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key !== 'Escape' || ev.defaultPrevented) return;
    if ((ev.target as HTMLElement | null)?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
    close();
  };
  const close = (): void => {
    if (closed) return;
    closed = true;
    clearArea?.();
    card.remove();
    document.removeEventListener('keydown', onKey);
    releaseFloat(close);
    if (preview?.close === close) preview = null;
    markRows();
  };
  takeFloat(close);
  preview = { id: s.id, close };
  markRows();
  document.addEventListener('keydown', onKey);
  card.querySelector('[data-a="close"]')!.addEventListener('click', close);

  let dto: ControlPreviewDto;
  try {
    dto = await previewControl(s.id);
  } catch (err) {
    if (closed) return;
    card.innerHTML = `${head}<small class="s">${esc(err instanceof Error ? err.message : 'Could not plan the change.')}</small>
      <div class="terr-btns">
        <button type="button" class="mini" data-a="dismiss">Dismiss</button>
        <button type="button" class="mini ghost" data-a="close">Close</button>
      </div>`;
    card.querySelector('[data-a="close"]')!.addEventListener('click', close);
    card.querySelector('[data-a="dismiss"]')!.addEventListener('click', async () => {
      await dismissAdvice('control', s.id);
      close();
      onDone();
    });
    return;
  }
  if (closed) return;
  const plan = dto.plan;
  clearArea = showAreaPreview(map, plan.geometry);
  const b = boundsOf(plan.geometry);
  if (b) map.fitBounds(b, { padding: 80, maxZoom: 10, duration: 700 });

  const colors = factionColors(colorSource?.() ?? []);
  const sw = (f: string | null): string =>
    `<span class="sw" style="background:${esc(colors[f ?? ''] ?? '#8a6d3b')}"></span>`;
  const takes = plan.takes.map((t) => `<div>Takes ${esc(fmtKm2(t.km2))} from ${sw(t.faction)}${
    esc(t.faction ?? t.name ?? '?')}${t.status === 'contested'
      ? ` (contested${t.contestedBy ? ` by ${esc(t.contestedBy)}` : ''})` : ''}</div>`).join('');
  const notes: string[] = [];
  if (plan.unheldKm2 >= 0.1) notes.push(`${fmtKm2(plan.unheldKm2)} of the district nobody holds stays out`);
  if (plan.absorbedKm2 >= 0.1) notes.push(`takes ${fmtKm2(plan.absorbedKm2)} of slivers along its edge`);
  const reports = dto.reports.map((r, i) => {
    const glyph = LIVE_KINDS[r.kind ?? '']?.glyph ?? '•';
    const when = r.at ? `${relTime(r.at)} ago` : '';
    const who = r.attributedTo
      ? `${esc(r.attributedTo)}${r.basis === 'stated' ? '' : ' (inferred)'}` : '';
    const chans = r.channels.slice(0, 4).map((c) => `@${esc(c)}`).join(', ')
      + (r.channels.length > 4 ? ` +${r.channels.length - 4}` : '');
    return `<button type="button" class="terr-ev" data-i="${i}"${r.lon == null ? ' disabled' : ''}
        title="${esc(r.summary ?? r.name ?? '')}">
      <span class="g">${esc(glyph)}</span>
      <span class="t"><b>${esc(r.name ?? r.kind ?? 'report')}</b>
        <small>${[who, chans, when].filter(Boolean).join(' · ')}</small></span></button>`;
  }).join('');
  const hidden = s.evidenceCount - dto.reports.length;
  card.innerHTML = `${head}
    <div class="terr-sum"><b>${esc(fmtKm2(plan.km2))} would change hands</b> — outlined in red on the map.
      ${notes.length ? `<small>${esc(notes.join(' · '))}</small>` : ''}</div>
    <div class="terr-takes">${takes || '<small class="s">Nothing to take — the ground already reads this way.</small>'}</div>
    <div class="terr-evs">
      <small class="s">${s.channels} channel${s.channels === 1 ? '' : 's'} · ${
        s.confidence ? `<span class="terr-conf terr-${esc(s.confidence)}">${esc(s.confidence)}</span> confidence` : 'unrated'}</small>
      ${reports}
      ${hidden > 0 ? `<small class="s">${hidden} more report${hidden === 1 ? '' : 's'} you cannot see or that aged out.</small>` : ''}
    </div>
    <small class="s">The whole district changes hands. If the reports are about part of it,
      draw that part instead.</small>
    <div class="terr-btns">
      <button type="button" class="mini commit" data-a="apply">Apply</button>
      <button type="button" class="mini" data-a="draw"
        title="Claim only the ground that moved: draw around it, then Apply">Draw it instead</button>
      <button type="button" class="mini" data-a="dismiss" title="Not a change of control">Dismiss</button>
      <button type="button" class="mini ghost" data-a="close">Close</button>
    </div>`;
  card.querySelectorAll<HTMLButtonElement>('.terr-ev').forEach((btn) => {
    btn.addEventListener('click', () => {
      const r = dto.reports[Number(btn.dataset.i)];
      if (r?.lon != null && r.lat != null) map.flyTo({ center: [r.lon, r.lat], zoom: Math.max(map.getZoom(), 10) });
    });
  });
  const busy = (on: boolean): void => {
    card.querySelectorAll<HTMLButtonElement>('.terr-btns button').forEach((x) => { x.disabled = on; });
  };
  card.querySelector('[data-a="close"]')!.addEventListener('click', close);
  card.querySelector('[data-a="apply"]')!.addEventListener('click', async () => {
    const apply = card.querySelector('[data-a="apply"]') as HTMLButtonElement;
    busy(true);
    apply.textContent = 'Applying…';
    try {
      const res = await applyControl(s.id);
      if (res.rev) bumpTiles(map, res.rev); else forceTileRefresh(map);
      toast(`${s.districtName ?? 'District'}: ${s.toStatus === 'contested'
        ? `contested by ${s.contestedBy ?? '?'}` : `now held by ${s.toFaction}`}.`);
      close();
      onDone();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not apply.');
      busy(false);
      apply.textContent = 'Apply';
    }
  });
  card.querySelector('[data-a="dismiss"]')!.addEventListener('click', async () => {
    busy(true);
    try {
      await dismissAdvice('control', s.id);
      close();
      onDone();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not dismiss.');
      busy(false);
    }
  });
  card.querySelector('[data-a="draw"]')!.addEventListener('click', () => {
    close();
    // The proposal's slot: who would hold it, and how. The claim merges
    // into that faction's territory in the country.
    startClaimFromCard({ attrs: {
      faction: s.toFaction, status: s.toStatus,
      contested_by: s.contestedBy ?? undefined, country_iso: s.countryIso,
    } });
  });
}
